// src/routes.tsx
import { createBrowserRouter, Navigate } from "react-router-dom";

import Login from "@pages/auth/login";
import Signup from "@pages/auth/signup";
import ConfirmEmail from "@pages/auth/confirm-email";
import MainLayout from "@/layouts/MainLayout";
import AuthLayout from "@/layouts/AuthLayout";
import PrivacyPolicy from "@/pages/privacypolicy";
import TermsOfService from "@/pages/terms";
import ForgotPassword from "@/pages/forgot-password";
import ResetPassword from "@/pages/reset-password";
import NotFound from "@/pages/not-found";
import { dashboardRoutes, supportRoutes, generateFlatRoutes } from "@/config/routes.tsx";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import WhatsAppOnboardingCallback from "@/pages/messaging/WhatsAppOnboardingCallback";
import { PermissionAwareRoute } from '@/components/auth/PermissionAwareRoute';
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary';

// Use Sentry's browser tracing integration (already configured in main.tsx)
// No need to wrap createBrowserRouter - Sentry's browserTracingIntegration handles routing automatically

export const router = createBrowserRouter([
    {
        path: "/",
        element: <Navigate to="/dashboard" replace />,
        errorElement: <RouteErrorBoundary />,
    },
    {
        path: "/auth",
        element: <AuthLayout />,
        errorElement: <RouteErrorBoundary />,
        children: [
            {
                path: "login",
                element: <Login />,
            },
            {
                path: "signup",
                element: <Signup />,
            },
            {
                path: "confirm-email",
                element: <ConfirmEmail />,
            },
            {
                path: "forgot-password",
                element: <ForgotPassword />,
            },
            {
                path: "reset-password",
                element: <ResetPassword />,
            },
        ],
    },
    {
        path: "/privacy-policy",
        element: <PrivacyPolicy />,
        errorElement: <RouteErrorBoundary />,
    },
    {
        path: "/terms-of-service",
        element: <TermsOfService />,
        errorElement: <RouteErrorBoundary />,
    },
    {
        path: "/dashboard",
        element: (
            <ProtectedRoute>
                <MainLayout />
            </ProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            index: true,
            element: <Navigate to="analytics" replace />
          },
          ...generateFlatRoutes(dashboardRoutes).map(route => {
            if (!route.element) return route;
            const Element = route.element;
            return {
              ...route,
              element: route.permission ? (
                <PermissionAwareRoute requiredPermission={route.permission}>
                  <Element />
                </PermissionAwareRoute>
              ) : (
                <Element />
              )
            };
          })
        ]
    },
    ...generateFlatRoutes(supportRoutes).map(route => {
      if (!route.element) return route;
      const Element = route.element;
      return {
        ...route,
        element: route.permission ? (
          <PermissionAwareRoute requiredPermission={route.permission}>
            <Element />
          </PermissionAwareRoute>
        ) : (
          <Element />
        )
      };
    }),
    {
        path: "/whatsapp-onboarding-callback",
        element: (
            <ProtectedRoute>
                <WhatsAppOnboardingCallback />
            </ProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
    },
    {
        path: "*",
        element: <NotFound />,
        errorElement: <RouteErrorBoundary />,
    },
], {
    future: {
        v7_normalizeFormMethod: true,
        v7_fetcherPersist: true,
        v7_relativeSplatPath: true,
        v7_partialHydration: true,
        v7_skipActionErrorRevalidation: true
    }
});
